import { useQuery } from '@tanstack/react-query';
import axiosLib from 'axios';

import { Loader } from '@/components/Loader';
import { NoData } from '@/components/ui/NoData';
import { useAxios } from '@/hooks/useAxios';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';
import type { GraphQueryResponse, TreeNode } from '@/types';

import { GraphResults } from './GraphResults';
import { TableResults } from './TableResults';
import { transformTreeNodeToFlat } from './utils/extractQueryResults';

interface QueryResultsProps {
  selectedQuery: SavedQuery | null;
}

/**
 * QueryResults - Executes graph queries and displays results
 *
 * Uses direct axios.post call with TanStack Query for state management.
 * Displays results in either table or graph view based on query type.
 * Handles errors inline without toast notifications.
 * Implements caching with 30s staleTime for performance optimization.
 *
 * Graph queries and table-tree queries set tree: true for nested response. For
 * table-tree queries (e.g., HAS_PORT), the TreeNode[] response is transformed
 * to flat array with relationship data joined.
 * Header is rendered by parent QueryBuilderWorkstation
 */
export const QueryResults = ({ selectedQuery }: QueryResultsProps) => {
  const { usernames } = useQueryBuilder();
  const axios = useAxios();

  const isGraphMode = selectedQuery?.type === 'graph';
  const isTableTreeMode = selectedQuery?.type === 'table-tree';

  // Extract selected emails for query execution
  const selectedEmails = usernames.filter(u => u.isSelected).map(u => u.email);

  // All hooks MUST be called before any conditional returns
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      'query-executor',
      selectedQuery?.id,
      selectedQuery?.type,
      selectedEmails,
      selectedQuery?.query, // Include query content for cache invalidation on changes
    ],
    queryFn: async () => {
      if (!selectedQuery) return null;

      let queryPayload = { ...selectedQuery.query };

      if (selectedEmails.length > 0)
        queryPayload = { ...queryPayload, usernames: selectedEmails };

      const needsTree = isGraphMode || isTableTreeMode;

      const response = await axios.post('/my', queryPayload, {
        timeout: 30000, // 30 second timeout
        params: needsTree ? { tree: true } : {},
      });

      return response.data as GraphQueryResponse | TreeNode[];
    },
    enabled: !!selectedQuery, // Only run query when selectedQuery exists
    staleTime: 30000, // Cache for 30 seconds (reduces API calls by 80-90%)
    gcTime: 300000, // Keep cache for 5 minutes during navigation
    retry: false, // Don't retry failed queries automatically
    placeholderData: undefined, // Prevent stale-while-revalidate from showing old data
  });

  // Early return AFTER all hooks are called
  if (!selectedQuery) {
    return null;
  }

  if (isLoading || isFetching) {
    return <Loader type={'spinner'} isLoading={isLoading || isFetching} />;
  }

  if (error) {
    const errorMessage = axiosLib.isAxiosError(error)
      ? error.response?.data?.message || error.message
      : 'An error occurred while executing the query';

    return (
      <div
        className="flex h-full w-full items-center justify-center p-6"
        role="alert"
        aria-live="polite"
        data-testid="query-executor-error"
      >
        <div className="text-center">
          <p className="text-chariot-text-primary font-semibold">
            Query Execution Failed
          </p>
          <p className="text-chariot-text-secondary mt-2 text-sm">
            {errorMessage}
          </p>
        </div>
      </div>
    );
  }

  // Handle empty results
  const isEmpty =
    !data ||
    (Array.isArray(data) && data.length === 0) ||
    (!Array.isArray(data) && (data as GraphQueryResponse).count === 0);

  if (isEmpty) {
    return (
      <div className="flex h-full items-center justify-center">
        <NoData title="No Results" description="This query returned no data" />
      </div>
    );
  }

  // Render view based on query type
  if (isGraphMode) {
    return <GraphResults key={selectedQuery?.id} data={data as TreeNode[]} />;
  }

  // Table view rendering
  if (isTableTreeMode && Array.isArray(data)) {
    // Transform TreeNode[] to flat array with relationship data joined
    const flatData = transformTreeNodeToFlat(data as TreeNode[]);

    // Extract entity type from first result
    const firstTree = (data as TreeNode[])[0];
    const entityType = firstTree?.node?.result
      ? (Object.keys(firstTree.node.result).find(
          key => key !== 'count' && key !== 'offset'
        ) as keyof GraphQueryResponse | undefined)
      : undefined;

    // Convert to GraphQueryResponse format for TableResults
    const graphResponse: GraphQueryResponse = {
      [entityType || 'assets']: flatData,
      count: flatData.length,
    } as GraphQueryResponse;

    return <TableResults key={selectedQuery?.id} data={graphResponse} />;
  }

  // Standard flat GraphQueryResponse
  const graphResponse = data as GraphQueryResponse;
  return <TableResults key={selectedQuery?.id} data={graphResponse} />;
};
